import { Icon } from "@certn/cascadia-react";
import {
  createContext,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CardConfiguration, CardState, CheckCardProps } from "./types";
import { configurations } from "./utils";

export interface CheckCardContextType extends CheckCardProps {
  isCardSelected: boolean;
  setIsCardSelected: (value: SetStateAction<boolean>) => void;
  showModal: boolean;
  setShowModal: (value: SetStateAction<boolean>) => void;
  checkCardConfig: CardConfiguration;
  isErrorType: boolean;
  isErrorOrInfoType: boolean;
}

export const CheckCardContext = createContext<CheckCardContextType | null>(
  null
);
export function useCheckCardContext() {
  const context = useContext(CheckCardContext);
  if (!context) {
    throw new Error(
      "useCheckCardContext must be used within CheckCardContextProvider"
    );
  }
  return context;
}

interface Props extends CheckCardProps, React.PropsWithChildren {}

export function CheckCardContextProvider(props: Props) {
  const [isCardSelected, setIsCardSelected] = useState<boolean>(
    props.isSelected || false
  );
  const [showModal, setShowModal] = useState(false);

  const getType = (): CardState => {
    if (isCardSelected) {
      return props.isDisabled ? "selected-disabled" : "selected";
    }
    return props.isDisabled ? "disabled" : "default";
  };
  const cardState = getType();
  const checkCardConfig = useMemo(() => {
    const baseConfig = configurations[cardState];
    if (
      props.showRemoveWhenSelected &&
      isCardSelected &&
      cardState === "selected" &&
      !props.isRequiredCheck
    ) {
      return {
        ...baseConfig,
        icon: (testID: string) => (
          <Icon
            type="standard"
            name="delete"
            size="space-5"
            color="icon-inverse"
            testID={`${testID}-delete-icon`}
          />
        ),
        iconBackground: "background-accent-red-strong",
      } satisfies CardConfiguration;
    }
    return baseConfig;
  }, [
    cardState,
    props.showRemoveWhenSelected,
    props.isRequiredCheck,
    isCardSelected,
  ]);
  const isErrorType = props.checkOrderingError?.type === "error";
  const isErrorOrInfoType = isErrorType;

  useEffect(() => {
    setIsCardSelected(props.isSelected ?? false);
  }, [props.isSelected]);

  return (
    <CheckCardContext.Provider
      value={{
        ...props,
        isCardSelected,
        setIsCardSelected,
        showModal,
        setShowModal,
        checkCardConfig,
        isErrorType,
        isErrorOrInfoType,
      }}
    >
      {props.children}
    </CheckCardContext.Provider>
  );
}
